import { c as _c } from "react/compiler-runtime";
import { useDebounceFn } from "ahooks";
import { gsap, Observer, ScrollTrigger } from "gsap/all";
import { Fragment, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { horizontalLoop } from "./gsapHelpers/horizontalLoop";
import { verticalLoop } from "./gsapHelpers/VerticalLoop";
import { createDebouncedEventListener } from "./ScreenContext";
import { useAnimation } from "./useAnimation";
import { useCombinedRefs } from "./useCombinedRefs";
import { withComponent } from "library/styled/withComponent";
import { Wrapper, Row, TwoButtons, OneButton as OneButton___raw } from "data:text/javascript;base64,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";
gsap.registerPlugin(Observer, ScrollTrigger);
export type MarqueeLoop = ReturnType<typeof horizontalLoop> | ReturnType<typeof verticalLoop>;
export function Marquee(t0) {
  const $ = _c(46);
  const {
    children,
    ArrowButton,
    BackArrowButton,
    className,
    marqueeSpeed: t1,
    reversed: t2,
    disableDrag: t3,
    disableSnap: t4,
    scrollVelocity,
    onChange,
    onLoopChange,
    loopRef,
    centerMode: t5,
    mode: t6
  } = t0;
  const marqueeSpeed = t1 === undefined ? 0 : t1;
  const reversed = t2 === undefined ? false : t2;
  const disableDrag = t3 === undefined ? false : t3;
  const disableSnap = t4 === undefined ? false : t4;
  const centerMode = t5 === undefined ? true : t5;
  const mode = t6 === undefined ? "horizontal" : t6;
  const rowRef = useRef(null);
  const internalLoopRef = useCombinedRefs(loopRef, undefined);
  const [numberNeeded, setNumberNeeded] = useState(1);
  const [refreshSignal, setRefreshSignal] = useState(0);
  let t7;
  let t8;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setRefreshSignal(_temp);
    t8 = {
      wait: 150
    };
    $[0] = t7;
    $[1] = t8;
  } else {
    t7 = $[0];
    t8 = $[1];
  }
  const {
    run: debouncedRefresh
  } = useDebounceFn(t7, t8);
  const latestOnChange = useRef(onChange);
  let t10;
  let t9;
  if ($[2] !== onChange) {
    t9 = () => {
      latestOnChange.current = onChange;
    };
    t10 = [onChange];
    $[2] = onChange;
    $[3] = t10;
    $[4] = t9;
  } else {
    t10 = $[3];
    t9 = $[4];
  }
  useLayoutEffect(t9, t10);
  const isVertical = mode === "vertical";
  let t11;
  let t12;
  if ($[5] !== centerMode || $[6] !== disableDrag || $[7] !== disableSnap || $[8] !== internalLoopRef || $[9] !== isVertical || $[10] !== marqueeSpeed || $[11] !== numberNeeded || $[12] !== reversed || $[13] !== scrollVelocity) {
    t11 = () => {
      if (!rowRef.current) {
        return;
      }
      const draggable = !disableDrag;
      const childCount = rowRef.current.children.length / numberNeeded;
      const firstLastChild = rowRef.current.children[childCount - 1];
      const firstLastEdgeMargin = isVertical ? Number(gsap.getProperty(firstLastChild ?? null, "marginBottom")) : Number(gsap.getProperty(firstLastChild ?? null, "marginRight"));
      const secondFirstChild = rowRef.current.children[childCount];
      const secondFirstEdgeMargin = isVertical ? Number(gsap.getProperty(secondFirstChild ?? null, "marginTop")) : Number(gsap.getProperty(secondFirstChild ?? null, "marginLeft"));
      const gap = firstLastChild && secondFirstChild ? isVertical ? Math.abs(firstLastChild.getBoundingClientRect().bottom - secondFirstChild.getBoundingClientRect().top) - firstLastEdgeMargin - secondFirstEdgeMargin : Math.abs(firstLastChild.getBoundingClientRect().right - secondFirstChild.getBoundingClientRect().left) - firstLastEdgeMargin - secondFirstEdgeMargin : 0;
      const loopConfig = {
        draggable,
        snap: disableSnap ? false : 1,
        paused: marqueeSpeed === 0,
        center: centerMode,
        speed: marqueeSpeed === 0 ? 2 : marqueeSpeed,
        reversed,
        repeat: -1,
        manageResize: false,
        onChange: (_, index) => {
          latestOnChange.current?.(index);
        }
      };
      const loop = isVertical ? verticalLoop(rowRef.current.children, {
        ...loopConfig,
        paddingBottom: gap
      }) : horizontalLoop(rowRef.current.children, {
        ...loopConfig,
        paddingRight: gap
      });
      internalLoopRef(loop);
      if (marqueeSpeed !== 0) {
        gsap.context(() => {
          const target = rowRef.current;
          if (!target) {
            return;
          }
          const visibilityObserver = new IntersectionObserver(t13 => {
            const [entry] = t13;
            if (entry?.isIntersecting) {
              loop.play();
            } else {
              loop.pause();
            }
          }, {
            threshold: 0
          });
          visibilityObserver.observe(target);
          return () => {
            visibilityObserver.disconnect();
          };
        });
      }
      if (marqueeSpeed === 0) {
        loop.toIndex(0);
        loop.timeScale(999);
        requestAnimationFrame(() => {
          loop.timeScale(1);
        });
      }
      if (scrollVelocity) {
        ScrollTrigger.create({
          onUpdate: self => {
            const delta = typeof scrollVelocity === "function" ? scrollVelocity(self.getVelocity()) : self.getVelocity() * (scrollVelocity / 1000);
            loop.scrollBy(delta);
          }
        });
      }
      if (!draggable) {
        return loop;
      }
      let tween;
      const onWheel = e => {
        if (isVertical) {
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) {
            return;
          }
        } else {
          if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
            return;
          }
        }
        if (loop.draggable.isDragging || loop.draggable.isThrowing) {
          return;
        }
        e.preventDefault();
        tween?.kill();
        gsap.killTweensOf(loop);
        loop.scrollBy(isVertical ? e.deltaY : e.deltaX);
      };
      gsap.context(() => {
        const row = rowRef.current;
        row?.addEventListener("wheel", onWheel);
        return () => {
          row?.removeEventListener("wheel", onWheel);
        };
      });
      Observer.create({
        target: rowRef.current,
        type: "wheel",
        onStop: () => {
          if (marqueeSpeed && !reversed) {
            loop.play();
          } else {
            if (marqueeSpeed && reversed) {
              loop.reverse();
            } else {
              tween = loop.toIndex(loop.current(), {
                ease: "power3.inOut",
                duration: 1
              });
            }
          }
        }
      });
      return loop;
    };
    t12 = [marqueeSpeed, reversed, disableDrag, disableSnap, scrollVelocity, numberNeeded, internalLoopRef, centerMode, isVertical];
    $[5] = centerMode;
    $[6] = disableDrag;
    $[7] = disableSnap;
    $[8] = internalLoopRef;
    $[9] = isVertical;
    $[10] = marqueeSpeed;
    $[11] = numberNeeded;
    $[12] = reversed;
    $[13] = scrollVelocity;
    $[14] = t11;
    $[15] = t12;
  } else {
    t11 = $[14];
    t12 = $[15];
  }
  let t13;
  if ($[16] !== refreshSignal) {
    t13 = {
      recreateOnResize: true,
      extraDeps: [refreshSignal]
    };
    $[16] = refreshSignal;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const {
    result: loop_0
  } = useAnimation(t11, t12, t13);
  const latestOnLoopChange = useRef(onLoopChange);
  let t14;
  let t15;
  if ($[18] !== onLoopChange) {
    t14 = () => {
      latestOnLoopChange.current = onLoopChange;
    };
    t15 = [onLoopChange];
    $[18] = onLoopChange;
    $[19] = t14;
    $[20] = t15;
  } else {
    t14 = $[19];
    t15 = $[20];
  }
  useLayoutEffect(t14, t15);
  let t16;
  let t17;
  if ($[21] !== loop_0) {
    t16 = () => {
      if (loop_0) {
        latestOnLoopChange.current?.(loop_0);
      }
    };
    t17 = [loop_0];
    $[21] = loop_0;
    $[22] = t16;
    $[23] = t17;
  } else {
    t16 = $[22];
    t17 = $[23];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[24] !== debouncedRefresh || $[25] !== isVertical) {
    t18 = () => {
      const update = () => {
        if (rowRef.current) {
          const totalChildrenSize = Array.from(rowRef.current.children).reduce((total, child) => total + (isVertical ? child.clientHeight : child.clientWidth), 0);
          setNumberNeeded(oldNumber => {
            const sizePerRepeat = totalChildrenSize / oldNumber;
            const screenSize = isVertical ? window.innerHeight : window.innerWidth;
            const newNumber = Math.ceil(screenSize / sizePerRepeat) + 1;
            return Number.isFinite(newNumber) && newNumber > 0 ? newNumber : oldNumber;
          });
        }
      };
      update();
      const elementsToObserve = Array.from(rowRef.current?.querySelectorAll("*") ?? []);
      const observer = new ResizeObserver(() => {
        update();
        debouncedRefresh();
      });
      for (const element of elementsToObserve) {
        observer.observe(element);
      }
      const listener = createDebouncedEventListener("resize", update);
      return () => {
        listener.cleanup();
        observer.disconnect();
      };
    };
    t19 = [debouncedRefresh, isVertical];
    $[24] = debouncedRefresh;
    $[25] = isVertical;
    $[26] = t18;
    $[27] = t19;
  } else {
    t18 = $[26];
    t19 = $[27];
  }
  useEffect(t18, t19);
  const hasButtons = Boolean(ArrowButton || BackArrowButton);
  const hasTwoButtons = Boolean(ArrowButton && BackArrowButton);
  const BackButton = BackArrowButton || ArrowButton;
  const ForwardButton = ArrowButton || BackArrowButton;
  const ButtonWrapper = hasTwoButtons ? TwoButtons : OneButton;
  const t20 = isVertical || undefined;
  let t21;
  if ($[28] !== children || $[29] !== numberNeeded) {
    let t22;
    if ($[31] !== children) {
      t22 = (__0, index_0) => <Fragment key={index_0}>{children}</Fragment>;
      $[31] = children;
      $[32] = t22;
    } else {
      t22 = $[32];
    }
    t21 = Array.from({
      length: numberNeeded
    }, t22);
    $[28] = children;
    $[29] = numberNeeded;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[33] !== t20 || $[34] !== t21) {
    t22 = <Row ref={rowRef} className="track" data-vertical={t20}>{t21}</Row>;
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== BackButton || $[37] !== ButtonWrapper || $[38] !== ForwardButton || $[39] !== hasButtons || $[40] !== loop_0) {
    t23 = hasButtons && <ButtonWrapper className="buttons">{BackButton && <BackButton onClick={() => loop_0?.previous({
        duration: 1,
        ease: "power3.out"
      })} />}{ForwardButton && <ForwardButton onClick={() => loop_0?.next({
        duration: 1,
        ease: "power3.out"
      })} />}</ButtonWrapper>;
    $[36] = BackButton;
    $[37] = ButtonWrapper;
    $[38] = ForwardButton;
    $[39] = hasButtons;
    $[40] = loop_0;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== className || $[43] !== t22 || $[44] !== t23) {
    t24 = <Wrapper className={className}>{t22}{t23}</Wrapper>;
    $[42] = className;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  return t24;
}
function _temp(s) {
  return s + 1;
}
const OneButton = withComponent(TwoButtons, OneButton___raw);